import { Flow, Step, StageId, FlowId, AiPrompt, UserFlowHelper, FormDefinition, FormField } from '../scripts/userflow';

/**
 * UserFlow Engine - Executes the userflow script step by step
 * Reads the script and follows its logic without deviation
 */

export interface StepExecutionContext {
  currentFlow: Flow;
  currentStep: Step;
  completedSteps: string[];
  userState: {
    rolesCount: number;
    usersCount: number;
    currentUserId?: string;
  };
}

export interface ExecutionResult {
  success: boolean;
  nextStep: Step | null;
  nextFlowId?: string;
  message?: string;
  action?: string; // 'guide', 'navigate', 'form', 'check', 'confirm'
  data?: any;
}

export class UserflowEngine {
  private currentStageId: StageId = 'initial_check';
  private currentFlowId: FlowId | null = null;
  private completedSteps: Set<string> = new Set();
  private executionHistory: { stepId: string; timestamp: Date }[] = [];
  private currentFlowDefinition: any = null; // Current flow definition (flows, forms, prompts)


  /**
   * Execute a single step from the script
   */
  async executeStep(
    step: Step,
    context: {
      rolesCount?: number;
      usersCount?: number;
      currentUserId?: string;
      flowDefinition?: any; // NEW: Flow definition passed from assistantManager
    }
  ): Promise<ExecutionResult> {
    this.completedSteps.add(step.id);
    this.executionHistory.push({ stepId: step.id, timestamp: new Date() });

    // Store the flow definition if provided
    if (context.flowDefinition) {
      this.currentFlowDefinition = context.flowDefinition;
    }

    const result: ExecutionResult = {
      success: true,
      nextStep: null,
      action: step.action,
    };

    switch (step.action) {
      case 'guide':
        return this.executeGuideAction(step, result);

      case 'check':
        return this.executeCheckAction(step, result, context);

      case 'navigate':
        return this.executeNavigateAction(step, result);

      case 'form':
        return this.executeFormAction(step, result);

      case 'confirm':
        return this.executeConfirmAction(step, result);

      case 'wait':
        return this.executeWaitAction(step, result);

      case 'ai_form':
        return this.executeAiFormAction(step, result);

      default:
        return { success: false, nextStep: null, message: `Unknown action type: ${step.action}` };
    }
  }

  /**
   * Execute 'guide' action - Show AI message
   */
  private executeGuideAction(step: Step, result: ExecutionResult): ExecutionResult {
    if (!step.guide) {
      return { success: false, nextStep: null, message: 'Guide config missing' };
    }

    if (!this.currentFlowDefinition?.aiPrompts) {
      return { success: false, nextStep: null, message: 'Flow definition not loaded' };
    }

    const promptId = step.guide.promptId;
    const prompt = this.currentFlowDefinition.aiPrompts[promptId];

    if (!prompt) {
      return { success: false, nextStep: null, message: `Prompt not found: ${promptId}` };
    }

    // Pick random variant for naturalness
    const message = UserFlowHelper.getPromptVariant(prompt);

    result.success = true;
    result.data = {
      message,
      format: step.guide.format,
      showOptions: step.guide.showOptions,
    };

    // Get next step
    if (step.nextStep) {
      result.nextStep = this.getNextStepObject(step.nextStep);
    }

    return result;
  }

  /**
   * Execute 'check' action - Check conditions and branch
   */
  private executeCheckAction(
    step: Step,
    result: ExecutionResult,
    context: { rolesCount: number; usersCount: number }
  ): ExecutionResult {
    if (!step.check) {
      return { success: false, nextStep: null, message: 'Check config missing' };
    }

    // Evaluate condition based on different patterns
    let conditionMet = false;
    const condition = step.check.condition.toLowerCase();
    const endpoint = step.check.endpoint.toLowerCase();

    // Check for roles
    if (endpoint.includes('roles') || condition.includes('role')) {
      // Check for >= 2 (need at least 2 roles: 1 admin auto-created + 1 user-created)
      if (condition.includes('>= 2') || condition.includes('atLeasttwo')) {
        conditionMet = context.rolesCount >= 2;
      }
      // Check for > 0 (at least 1 role)
      else if (condition.includes('> 0') || condition.includes('atLeastOne')) {
        conditionMet = context.rolesCount > 0;
      } else {
        conditionMet = context.rolesCount > 0;
      }
    }
    // Check for users/invites
    else if (endpoint.includes('users') || endpoint.includes('invitations') || condition.includes('user') || condition.includes('invite')) {
      // Check for >= 1 (at least 1 user invited)
      if (condition.includes('>= 1') || condition.includes('atLeastOne')) {
        conditionMet = context.usersCount >= 1;
      }
      // Generic > 0 check
      else if (condition.includes('> 0')) {
        conditionMet = context.usersCount > 0;
      } else {
        conditionMet = context.usersCount > 0;
      }
    }

    const branch = conditionMet ? step.check.branches.true : step.check.branches.false;
    result.nextStep = this.getNextStepObject(branch);
    result.data = { condition: step.check.condition, conditionMet };

    console.log(`[UserflowEngine] Check step "${step.id}": condition="${condition}" rolesCount=${context.rolesCount} usersCount=${context.usersCount} → ${conditionMet ? 'TRUE' : 'FALSE'}`);
    return result;
  }

  /**
   * Execute 'navigate' action - Guide user to navigate
   */
  private executeNavigateAction(step: Step, result: ExecutionResult): ExecutionResult {
    if (!step.navigate) {
      return { success: false, nextStep: null, message: 'Navigate config missing' };
    }

    result.data = {
      path: step.navigate.path,
      label: step.navigate.label,
      description: step.navigate.description,
      uiSteps: step.navigate.uiSteps || [],
      uiInteractions: step.uiInteractions || [],
    };

    // Get next step
    if (step.nextStep) {
      result.nextStep = this.getNextStepObject(step.nextStep);
    }

    return result;
  }

  /**
   * Execute 'form' action - Prepare form for filling
   */
  private executeFormAction(step: Step, result: ExecutionResult): ExecutionResult {
    if (!step.form) {
      return { success: false, nextStep: null, message: 'Form config missing' };
    }

    if (!this.currentFlowDefinition?.forms) {
      return { success: false, nextStep: null, message: 'Flow definition not loaded' };
    }

    const formId = step.form.formId;
    const form = this.currentFlowDefinition.forms[formId];

    if (!form) {
      return { success: false, nextStep: null, message: `Form not found: ${formId}` };
    }

    result.data = {
      form: form,
      endpoint: step.form.endpoint,
      method: step.form.method,
      fields: step.form.fields,
    };

    // Get next step
    if (step.nextStep) {
      result.nextStep = this.getNextStepObject(step.nextStep);
    }

    return result;
  }

  /**
   * Execute 'confirm' action - Ask for user confirmation
   */
  private executeConfirmAction(step: Step, result: ExecutionResult): ExecutionResult {
    if (!step.confirm) {
      return { success: false, nextStep: null, message: 'Confirm config missing' };
    }

    if (!this.currentFlowDefinition?.aiPrompts) {
      return { success: false, nextStep: null, message: 'Flow definition not loaded' };
    }

    const promptId = step.confirm.promptId;
    const prompt = this.currentFlowDefinition.aiPrompts[promptId];

    if (!prompt) {
      return { success: false, nextStep: null, message: `Prompt not found: ${promptId}` };
    }

    const message = UserFlowHelper.getPromptVariant(prompt);

    result.data = {
      message,
      options: step.confirm.options,
    };

    return result;
  }

  /**
   * Execute 'wait' action - Just wait (for UI to load)
   */
  private executeWaitAction(step: Step, result: ExecutionResult): ExecutionResult {
    result.data = {
      waitMs: 500,
      message: 'Waiting for page to load...',
    };

    // Get next step
    if (step.nextStep) {
      result.nextStep = this.getNextStepObject(step.nextStep);
    }

    return result;
  }

  /**
   * Execute 'ai_form' action - Conversational form filling with AI
   */
  private executeAiFormAction(step: Step, result: ExecutionResult): ExecutionResult {
    if (!step.ai_form) {
      return { success: false, nextStep: null, message: 'AI form config missing' };
    }

    // Return the form configuration so UserflowContext can start the AI conversation
    result.success = true;
    result.data = {
      metadataKey: step.ai_form.metadataKey,
      endpoint: step.ai_form.endpoint,
      method: step.ai_form.method,
    };

    // Get next step (will be executed after form submission)
    if (step.nextStep) {
      result.nextStep = this.getNextStepObject(step.nextStep);
    }

    return result;
  }

  /**
   * Get the next step as a Step object
   */
  private getNextStepObject(nextStep: string | Step | undefined): Step | null {
    if (!nextStep) return null;

    if (typeof nextStep === 'string') {
      // It's a step ID, find it in flows
      return this.findStepById(nextStep);
    }

    // It's already a Step object
    return nextStep as Step;
  }

  /**
   * Find a step by ID across all flows in current flow definition
   */
  private findStepById(stepId: string): Step | null {
    if (!this.currentFlowDefinition?.flows) {
      console.warn('[UserflowEngine] No flow definition loaded to find step:', stepId);
      return null;
    }

    for (const flow of Object.values(this.currentFlowDefinition.flows)) {
      const step = flow.steps.find(s => s.id === stepId);
      if (step) return step;
    }
    return null;
  }

  /**
   * Get a complete flow by ID from current flow definition
   */
  getFlowById(flowId: FlowId): Flow | null {
    if (!this.currentFlowDefinition?.flows) {
      return null;
    }
    return this.currentFlowDefinition.flows[flowId] || null;
  }

  /**
   * Get form definition by ID from current flow definition
   */
  getForm(formId: string): FormDefinition | null {
    if (!this.currentFlowDefinition?.forms) {
      return null;
    }
    return this.currentFlowDefinition.forms[formId] || null;
  }

  /**
   * Get all fields for a form
   */
  getFormFields(formId: string): FormField[] {
    const form = this.getForm(formId);
    return form?.fields || [];
  }

  /**
   * Get AI prompt from current flow definition
   */
  getPrompt(promptId: string): AiPrompt | null {
    if (!this.currentFlowDefinition?.aiPrompts) {
      return null;
    }
    return this.currentFlowDefinition.aiPrompts[promptId] || null;
  }

  /**
   * Get random prompt variant
   */
  getPromptVariant(promptId: string): string | null {
    const prompt = this.getPrompt(promptId);
    if (!prompt) return null;
    return UserFlowHelper.getPromptVariant(prompt);
  }

  /**
   * Get current stage
   */
  getCurrentStage(): StageId {
    return this.currentStageId;
  }

  /**
   * Get current flow
   */
  getCurrentFlow(): Flow | null {
    return this.currentFlowId ? this.getFlowById(this.currentFlowId) : null;
  }

  /**
   * Get completed steps
   */
  getCompletedSteps(): string[] {
    return Array.from(this.completedSteps);
  }

  /**
   * Reset all state (for testing or re-onboarding)
   */
  resetState() {
    this.currentStageId = 'initial_check';
    this.currentFlowId = null;
    this.completedSteps.clear();
    this.executionHistory = [];
  }
}

// Export singleton instance
export const userflowEngine = new UserflowEngine();
